import { Activity, ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// third party
import { FormattedMessage } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

// project imports
import { ThemeDirection } from 'config';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { drawerWidth, gridSpacing } from 'store/constant';
import useConfig from 'hooks/useConfig';

// real, permission/module-filtered menu (same source + filters the sidebar uses) — replaces the
// Berry demo link list so the mega-menu only shows sections this user can actually reach.
import menuItems from 'menu-items';
import { NavItemType } from 'types';
import { useEntitlementsStore, useMe } from '@zercode/auth';
import { useBranding, brandDisplayName } from '@zercode/theme';
import { authApi } from '../../../../../lib/zercode';
import { filterMenuByModules, filterMenuByPermissions } from '../../../../../lib/menuPermissions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IconAccessPoint } from '@tabler/icons-react';

interface HeaderAvatarProps extends AvatarProps {
  children: ReactNode;
}

function HeaderAvatar({ children, sx, ref, ...others }: HeaderAvatarProps) {
  const theme = useTheme();

  return (
    <Avatar
      ref={ref}
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
        display: { xs: 'none', md: 'flex' },
        color: theme.vars.palette.secondary.dark,
        background: theme.vars.palette.secondary.light,
        '&:hover, &[aria-controls="menu-list-grow"]': {
          color: theme.vars.palette.secondary.light,
          background: theme.vars.palette.secondary.dark
        },
        ...theme.applyStyles('dark', {
          color: theme.vars.palette.secondary.main,
          background: theme.vars.palette.dark.main,
          '&:hover, &[aria-controls="menu-list-grow"]': {
            color: theme.vars.palette.secondary.light,
            background: theme.vars.palette.secondary.main
          }
        })
      }}
      {...others}
    >
      {children}
    </Avatar>
  );
}

// ==============================|| SEARCH INPUT - MEGA MENu||============================== //

export default function MegaMenuSection() {
  const theme = useTheme();
  const {
    state: { themeDirection }
  } = useConfig();

  // Same permission + licensed-module filtering the sidebar applies (see MenuList). Entitlements
  // are already seeded there; we only READ them here. /me feeds the permission filter.
  const meQuery = useMe(authApi);
  const modules = useEntitlementsStore((s) => s.modules);
  const permitted = filterMenuByPermissions(menuItems.items, meQuery.data?.permissions ?? []);
  const groups = filterMenuByModules(permitted, modules).filter((group) => group.type === 'group');

  // tenant branding for the panel's left cell (replaces Berry's promo Banner)
  const branding = useBranding();
  const logo = theme.palette.mode === 'dark' ? (branding.logo.dark ?? branding.logo.light) : (branding.logo.light ?? branding.logo.dark);
  const brandName = brandDisplayName(branding);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <HeaderAvatar
        variant="rounded"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <IconAccessPoint stroke={1.5} size="20px" />
      </HeaderAvatar>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        sx={{ ...(themeDirection === ThemeDirection.RTL && { right: { md: '-170px !important', lg: '-300px !important' } }) }}
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [150, 20]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  width: {
                    md: `calc(100vw - 100px)`,
                    lg: `calc(100vw - ${drawerWidth + 100}px)`,
                    xl: `calc(100vw - ${drawerWidth + 140}px)`
                  },
                  maxWidth: { xl: 900, md: 764 }
                }}
              >
                <Activity mode={open ? 'visible' : 'hidden'}>
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                    sx={{ overflow: { p: 1, xs: 'visible', md: 'hidden' } }}
                  >
                    <Grid container spacing={gridSpacing}>
                      <Grid size={{ md: 4 }}>
                        <Box
                          sx={{
                            height: '100%',
                            minHeight: 200,
                            borderRadius: 3,
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            textAlign: 'center',
                            bgcolor: 'primary.light',
                            ...theme.applyStyles('dark', { bgcolor: 'dark.main' })
                          }}
                        >
                          {logo ? (
                            <Box
                              component="img"
                              src={logo}
                              alt={branding.logo.altText ?? brandName}
                              sx={{ maxWidth: '80%', maxHeight: 64, objectFit: 'contain' }}
                            />
                          ) : (
                            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 24 }}>
                              {brandName.charAt(0).toUpperCase()}
                            </Avatar>
                          )}
                          <Typography variant="h4" sx={{ color: 'primary.dark', ...theme.applyStyles('dark', { color: 'text.primary' }) }}>
                            {brandName}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ md: 8 }}>
                        <Grid
                          container
                          spacing={gridSpacing}
                          sx={{
                            pt: 3,
                            '& .MuiListItemButton-root:hover': {
                              bgcolor: 'transparent',
                              '& .MuiTypography-root': {
                                color: 'secondary.main'
                              }
                            },
                            '& .MuiListItemIcon-root': {
                              minWidth: 16
                            }
                          }}
                        >
                          {groups.map((group) => {
                            // groups with children → list the children; a bare group with a url
                            // (e.g. Dashboard) → a single link to itself.
                            const entries = (group.children?.length ? group.children : [group]).filter(
                              (entry: NavItemType) => entry.url
                            );
                            return (
                              <Grid key={group.id} size={3}>
                                <List
                                  component="nav"
                                  aria-labelledby={`list-${group.id}`}
                                  sx={{
                                    // cap each section so a large one (e.g. Eğitim) scrolls within its
                                    // own column instead of stretching the whole panel; the sticky
                                    // ListSubheader keeps the section title pinned while items scroll.
                                    maxHeight: 180,
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: 6 },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 }
                                  }}
                                  subheader={
                                    <ListSubheader id={`list-${group.id}`}>
                                      <Typography variant="subtitle1">
                                        <FormattedMessage id={group.title as string} />
                                      </Typography>
                                    </ListSubheader>
                                  }
                                >
                                  {entries.map((entry: NavItemType) => (
                                    <ListItemButton component={Link} to={entry.url!} key={entry.id} onClick={handleToggle}>
                                      <ListItemIcon>
                                        <FiberManualRecordIcon sx={{ fontSize: '0.5rem' }} />
                                      </ListItemIcon>
                                      <ListItemText primary={<FormattedMessage id={entry.title as string} />} />
                                    </ListItemButton>
                                  ))}
                                </List>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    </Grid>
                  </MainCard>
                </Activity>
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
